import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Rocket, Brain, Users, Award, TrendingUp, Target, Zap, Code, ArrowRight, CheckCircle, Sparkles, Star } from 'lucide-react';
import { useAppStore } from '../store/appStore';

const SummaryPage = () => {
  const { setCurrentPage } = useAppStore();
  const videoRef = useRef(null);

  const journeyMilestones = [
    {
      year: '2024',
      title: 'Innovation Champion',
      icon: Award,
      description: 'Won 2024 Innovation Challenge with Rule Master (RAG-based rule generation)',
      color: 'from-yellow-500 to-amber-500',
      highlights: ['RAG Technology', 'Natural Language Control', 'Knowledge Graph Research']
    },
    {
      year: '2024-2025',
      title: 'SSA AI Pioneer',
      icon: Sparkles,
      description: 'Voluntarily led SSA AI initiative from ground up, connecting teams and driving innovation',
      color: 'from-purple-500 to-pink-500',
      highlights: ['Cross-Department Bridge', 'AI Democratization', 'Team Leadership']
    },
    {
      year: '2025',
      title: 'People Star',
      icon: Star,
      description: 'Recognized as 2025 People Star for exceptional contributions and technical leadership',
      color: 'from-blue-500 to-cyan-500',
      highlights: ['10-Year Veteran', 'Technical Excellence', 'Innovation Leadership']
    }
  ];

  const impactCategories = [
    {
      category: 'Business Impact',
      icon: TrendingUp,
      color: 'from-green-500 to-emerald-500',
      metrics: [
        { label: 'Efficiency Gain', value: '50%', project: 'Tech Refresh' },
        { label: 'Time Reduction', value: '90%', project: 'Product Transformation Robot' },
        { label: 'Performance Boost', value: '8.2x', project: 'IUL SPOCK Engine' },
        { label: 'Productivity', value: '300%', project: 'Email Assistant' }
      ]
    },
    {
      category: 'Technical Innovation',
      icon: Zap,
      color: 'from-purple-500 to-pink-500',
      metrics: [
        { label: 'AI Projects', value: '5+', project: '2025 Innovation' },
        { label: 'MCP Integrations', value: '10+', project: 'BSD CLI & Email' },
        { label: 'Research Areas', value: '3', project: 'GraphRAG, Workflow, CLI' },
        { label: 'Production Systems', value: '4', project: 'Real-world Integration' }
      ]
    },
    {
      category: 'Leadership & Collaboration',
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
      metrics: [
        { label: 'Major Projects', value: '9', project: 'Across Domains' },
        { label: 'Collaborators', value: '15+', project: 'Internal & External' },
        { label: 'Teams Connected', value: '6', project: 'Cross-Department' },
        { label: 'Knowledge Shared', value: '100%', project: 'Documentation & Mentorship' }
      ]
    }
  ];

  const coreCompetencies = [
    {
      title: 'AI & Machine Learning',
      icon: Brain,
      skills: ['RAG Implementation', 'LLM Integration', 'GraphRAG Research', 'Multimodal AI', 'Agent Systems', 'MCP Protocol'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Full-Stack Development',
      icon: Code,
      skills: ['React 18', 'FastAPI', 'Microservices', 'Async Processing', 'RESTful APIs', 'WebSocket'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'System Architecture',
      icon: Target,
      skills: ['Scalable Design', 'Cloud Integration', 'Security-First', 'Performance Optimization', 'Distributed Systems', 'API Design'],
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Technical Leadership',
      icon: Award,
      skills: ['Team Building', 'Strategic Planning', 'Knowledge Sharing', 'Mentorship', 'Innovation Management', 'Cross-Department Collaboration'],
      color: 'from-yellow-500 to-orange-500'
    }
  ];

  const projectHighlights = [
    {
      title: 'iSpeed Projects',
      icon: Rocket,
      description: 'Delivered 4 major projects with exceptional business impact',
      projects: ['IUL (8.2x faster)', 'Singpass (80% time saved)', 'FAPL (50% faster)', 'Adobe Offline Signature'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'AI Innovation 2025',
      icon: Brain,
      description: 'Five groundbreaking AI projects transforming business operations',
      projects: ['Tech Refresh (50% efficiency)', 'BSD CLI (76% token savings)', 'Email Assistant (75% faster)', 'TPR (90% time saved)', 'RFP Assistant (85% time saved)'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Research Excellence',
      icon: Zap,
      description: 'Post-competition research pushing AI boundaries',
      projects: ['GraphRAG Exploration', 'AI Workflow Research', 'CLI Agent Framework', 'MCP Ecosystem'],
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="page" style={{ overflowY: 'auto' }}>
      <div className="h-screen p-8 pb-20">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setCurrentPage('landing')}
          className="fixed top-8 left-8 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-4 py-2 flex items-center gap-2 z-10"
        >
          <ArrowRight className="w-5 h-5 text-white rotate-180" />
          <span className="text-white text-sm font-semibold">Back</span>
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 mt-16"
        >
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            2025 Summary
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A Year of Innovation, Leadership, and Transformative Impact
          </p>
        </motion.div>


        {/* Impact Metrics */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
            <Target className="w-8 h-8 text-green-400" />
            Measurable Impact
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {impactCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 rounded-xl border border-white/10 p-6"
                >
                  <div className={`bg-gradient-to-br ${category.color} p-3 rounded-lg w-fit mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{category.category}</h3>
                  <div className="space-y-3">
                    {category.metrics.map((metric, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-400 text-xs">{metric.label}</span>
                          <span className={`bg-gradient-to-r ${category.color} text-white text-sm font-bold px-2 py-1 rounded`}>
                            {metric.value}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs">{metric.project}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Project Highlights */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
            <Rocket className="w-8 h-8 text-orange-400" />
            Project Portfolio
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projectHighlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${highlight.color} rounded-xl p-6`}
                >
                  <div className="bg-white/20 rounded-lg w-fit p-3 mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{highlight.title}</h3>
                  <p className="text-white/90 text-sm mb-4">{highlight.description}</p>
                  <ul className="space-y-2">
                    {highlight.projects.map((project, idx) => (
                      <li key={idx} className="text-white text-sm flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{project}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto text-center"
        >
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-8 border border-purple-500/30">
            <h3 className="text-3xl font-bold text-white mb-5">
              "Innovation is not just tech—it's sparking people, linking teams, sharing knowledge, transforming business together."
            </h3>
            <p className="text-gray-300 mb-6">
              Thank you for reviewing my 2025 People Star journey. Let's continue innovating together!
            </p>

            {/* Architecture Image */}
            <div className="relative bg-gray-900/50 rounded-xl overflow-hidden max-w-4xl mx-auto">
              <img
                src="/pic/summary.webp"
                alt="Complete Technical Architecture"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SummaryPage;
